import React, { useState, useEffect, useCallback } from 'react';
import {
  getRecurringInvoices, getRecurringInvoiceStats, createRecurringInvoice,
  updateRecurringInvoice, deleteRecurringInvoice, pauseRecurringInvoice,
  resumeRecurringInvoice, generateRecurringNow, getRecurringInvoiceHistory,
  getClients
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  RefreshCw, Plus, Search, MoreVertical, Pause, Play, Zap, History,
  Trash2, Edit, Loader2, Calendar, Clock, FileText, AlertTriangle,
  CheckCircle2, XCircle, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom Interval' }
];

const SEND_MODES = [
  { value: 'draft', label: 'Create as Draft' },
  { value: 'send', label: 'Send Automatically' }
];

export default function RecurringInvoicesPage() {
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [historyData, setHistoryData] = useState({ items: [], total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', client_id: '', frequency: 'monthly', custom_interval_days: 30,
    start_date: new Date().toISOString().split('T')[0], end_date: '',
    auto_send_mode: 'draft', payment_terms: 'Due on Receipt', vat_percentage: 5,
    discount_type: 'None', discount_value: 0, notes: '', terms_conditions: '',
    items: [{ _key: 'init-0', description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, discount_percentage: 0, vat_applicable: true }]
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (frequencyFilter !== 'all') params.frequency = frequencyFilter;
      const [templatesRes, statsRes] = await Promise.all([
        getRecurringInvoices(params),
        getRecurringInvoiceStats()
      ]);
      setTemplates(templatesRes.data.items || []);
      setStats(statsRes.data);
    } catch (err) {
      setError('Failed to load recurring invoices');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, frequencyFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    getClients({ limit: 500 }).then(res => setClients(res.data.items || [])).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({
      name: '', client_id: '', frequency: 'monthly', custom_interval_days: 30,
      start_date: new Date().toISOString().split('T')[0], end_date: '',
      auto_send_mode: 'draft', payment_terms: 'Due on Receipt', vat_percentage: 5,
      discount_type: 'None', discount_value: 0, notes: '', terms_conditions: '',
      items: [{ _key: 'reset-0', description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, discount_percentage: 0, vat_applicable: true }]
    });
  };

  const handleCreate = () => { resetForm(); setEditingTemplate(null); setShowCreate(true); };

  const handleEdit = (template) => {
    setForm({
      name: template.name || '', client_id: template.client_id || '',
      frequency: template.frequency || 'monthly',
      custom_interval_days: template.custom_interval_days || 30,
      start_date: template.start_date || '', end_date: template.end_date || '',
      auto_send_mode: template.auto_send_mode || 'draft',
      payment_terms: template.payment_terms || 'Due on Receipt',
      vat_percentage: template.vat_percentage || 5,
      discount_type: template.discount_type || 'None',
      discount_value: template.discount_value || 0,
      notes: template.notes || '', terms_conditions: template.terms_conditions || '',
      items: template.items?.length ? template.items.map((it, i) => ({ ...it, _key: `edit-${i}` })) : [{ _key: 'edit-new', description: '', quantity: 1, unit_price: 0 }]
    });
    setEditingTemplate(template);
    setShowCreate(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.client_id || !form.items.some(i => i.description)) {
      toast.error('Please fill in template name, client, and at least one item');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, items: form.items.filter(i => i.description) };
      if (payload.frequency !== 'custom') delete payload.custom_interval_days;
      if (!payload.end_date) delete payload.end_date;
      if (editingTemplate) {
        await updateRecurringInvoice(editingTemplate.id, payload);
        toast.success('Template updated');
      } else {
        await createRecurringInvoice(payload);
        toast.success('Recurring invoice template created');
      }
      setShowCreate(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePauseResume = async (template) => {
    try {
      if (template.enabled) {
        await pauseRecurringInvoice(template.id);
        toast.success('Template paused');
      } else {
        await resumeRecurringInvoice(template.id);
        toast.success('Template resumed');
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to update template status');
    }
  };

  const handleGenerateNow = async (template) => {
    try {
      const res = await generateRecurringNow(template.id);
      toast.success(`Invoice ${res.data.invoice_no} generated (AED ${res.data.amount?.toFixed(2)})`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRecurringInvoice(id);
      toast.success('Template deleted');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const handleViewHistory = async (template) => {
    setShowHistory(template);
    setHistoryLoading(true);
    try {
      const res = await getRecurringInvoiceHistory(template.id);
      setHistoryData(res.data);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const makeItemKey = () => `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const addItem = () => {
    setForm(f => ({ ...f, items: [...f.items, { _key: makeItemKey(), description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, discount_percentage: 0, vat_applicable: true }] }));
  };

  const updateItem = (idx, field, value) => {
    setForm(f => ({
      ...f, items: f.items.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const calcTotal = () => {
    const subtotal = form.items.reduce((sum, item) => {
      const line = (item.quantity || 0) * (item.unit_price || 0);
      const disc = line * ((item.discount_percentage || 0) / 100);
      return sum + line - disc;
    }, 0);
    const invDisc = form.discount_type === 'Percentage' ? subtotal * (form.discount_value / 100)
      : form.discount_type === 'Fixed' ? form.discount_value : 0;
    const taxable = subtotal - invDisc;
    const vat = taxable * ((form.vat_percentage || 0) / 100);
    return { subtotal, invDisc, taxable, vat, total: taxable + vat };
  };

  return (
    <div data-testid="recurring-invoices-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Recurring Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Automate invoice generation with customizable schedules</p>
        </div>
        <Button data-testid="recurring-invoices-create-template-button" onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" /> New Template
        </Button>
      </div>

      {/* KPI Strip */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Active Templates', value: stats.active_templates, icon: RefreshCw, color: 'text-primary' },
            { label: 'Next 7 Days', value: stats.next_7_days_scheduled, icon: Calendar, color: 'text-blue-600' },
            { label: 'Auto-send Enabled', value: stats.auto_send_enabled, icon: Zap, color: 'text-amber-600' },
            { label: 'Failures (30d)', value: stats.failures_last_30_days, icon: AlertTriangle, color: stats.failures_last_30_days > 0 ? 'text-red-500' : 'text-emerald-600' }
          ].map((kpi) => (
            <Card key={kpi.label} className="relative overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
                    <p className="text-2xl font-bold mt-1 font-mono tabular-nums">{kpi.value}</p>
                  </div>
                  <kpi.icon className={cn('h-8 w-8 opacity-60', kpi.color)} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="recurring-invoices-search-input"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select data-testid="recurring-invoices-status-filter-select" value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="enabled">Enabled</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
            <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Frequency" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Frequencies</SelectItem>
                {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error} <Button variant="link" size="sm" onClick={fetchData}>Retry</Button></AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : templates.length === 0 ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold">No recurring invoices yet</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Create a template to automate your billing.</p>
              <Button onClick={handleCreate}><Plus className="h-4 w-4 mr-2" /> Create Template</Button>
            </div>
          ) : (
            <Table data-testid="recurring-invoices-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Auto-send</TableHead>
                  <TableHead>Next Run</TableHead>
                  <TableHead>Last Generated</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.client_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.enabled ? 'default' : 'secondary'}
                        className={t.enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                        {t.enabled ? 'Enabled' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{t.frequency === 'custom' ? `Every ${t.custom_interval_days}d` : t.frequency}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={t.auto_send_mode === 'send' ? 'border-amber-300 text-amber-700' : ''}>
                        {t.auto_send_mode === 'send' ? 'Auto-send' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{t.next_run_at || '-'}</TableCell>
                    <TableCell className="text-sm">{t.last_generated_at ? new Date(t.last_generated_at).toLocaleDateString() : 'Never'}</TableCell>
                    <TableCell className="font-mono">{t.generated_count || 0}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid="recurring-invoices-row-actions-button" variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(t)}>
                            <Edit className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid="recurring-invoices-pause-resume-button" onClick={() => handlePauseResume(t)}>
                            {t.enabled ? <><Pause className="h-4 w-4 mr-2" /> Pause</> : <><Play className="h-4 w-4 mr-2" /> Resume</>}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleGenerateNow(t)}>
                            <Zap className="h-4 w-4 mr-2" /> Generate Now
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid="recurring-invoices-history-open-button" onClick={() => handleViewHistory(t)}>
                            <History className="h-4 w-4 mr-2" /> View History
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteConfirm(t)}>
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Recurring Invoice'}</DialogTitle>
            <DialogDescription>Configure the invoice template and schedule.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input data-testid="recurring-invoices-name-input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Monthly Service Fee" />
              </div>
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="recurring-invoices-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.trade_name || c.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm(f => ({ ...f, frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === 'custom' && (
                <div className="space-y-2">
                  <Label>Interval (days)</Label>
                  <Input type="number" min="1" value={form.custom_interval_days} onChange={(e) => setForm(f => ({ ...f, custom_interval_days: parseInt(e.target.value) || 1 }))} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>

            {/* Send Mode */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>When Generated</Label>
                <Select value={form.auto_send_mode} onValueChange={(v) => setForm(f => ({ ...f, auto_send_mode: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEND_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setForm(f => ({ ...f, payment_terms: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 60'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>VAT %</Label>
                <Input type="number" min="0" max="100" value={form.vat_percentage} onChange={(e) => setForm(f => ({ ...f, vat_percentage: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>

            <Separator />

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Invoice Items</Label>
                <Button variant="outline" size="sm" onClick={addItem}><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
              </div>
              <div className="space-y-3">
                {form.items.map((item, idx) => (
                  <div key={item._key || `item-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      {idx === 0 && <Label className="text-xs">Description</Label>}
                      <Input placeholder="Service description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Qty</Label>}
                      <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} />
                    </div>
                    <div className="col-span-3">
                      {idx === 0 && <Label className="text-xs">Unit Price</Label>}
                      <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      {form.items.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeItem(idx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Totals Preview */}
              {(() => {
                const { subtotal, invDisc, vat, total } = calcTotal();
                return (
                  <div className="mt-4 p-3 bg-muted/40 rounded-lg text-sm space-y-1">
                    <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">AED {subtotal.toFixed(2)}</span></div>
                    {invDisc > 0 && <div className="flex justify-between text-muted-foreground"><span>Discount</span><span className="font-mono">-AED {invDisc.toFixed(2)}</span></div>}
                    <div className="flex justify-between text-muted-foreground"><span>VAT ({form.vat_percentage}%)</span><span className="font-mono">AED {vat.toFixed(2)}</span></div>
                    <Separator />
                    <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono">AED {total.toFixed(2)}</span></div>
                  </div>
                );
              })()}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="recurring-invoices-save-button" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingTemplate ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!showHistory} onOpenChange={() => setShowHistory(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generation History: {showHistory?.name}</DialogTitle>
            <DialogDescription>View all invoices generated from this template.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="space-y-3 py-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : historyData.items.length === 0 ? (
            <div className="py-8 text-center">
              <History className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">No invoices generated yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Triggered By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyData.items.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="font-mono text-sm">{new Date(run.run_at).toLocaleDateString()}</TableCell>
                    <TableCell className="font-medium">{run.invoice_no || '-'}</TableCell>
                    <TableCell className="font-mono">{run.amount ? `AED ${run.amount.toFixed(2)}` : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={run.result_status === 'failed' ? 'destructive' : 'default'}
                        className={run.result_status !== 'failed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                        {run.result_status === 'failed' ? <XCircle className="h-3 w-3 mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        {run.result_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{run.triggered_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently disable and remove "{deleteConfirm?.name}". Previously generated invoices will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => handleDelete(deleteConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
